import { useMemo } from "react";

import { useWebviewContext } from "helpers/webview/WebviewContext";

import { routes } from "utilities/routes";

import { type SelectionListContainerTabs } from "entity/selection/SelectionListContainer/SelectionListContainer.types";
import { type SelectionOptionStateOverride } from "entity/selectionOptions/SelectionOption/SelectionOption.types";

/**
 * Returns webview-aware URL builders for the selection options flow.
 *
 * In a webview shell (`useWebviewContext()` returns a value) the builders generate webview
 * URLs (`/webview/<platform>/<mobileVersion>/...`). In the SPA they generate the regular
 * SPA URLs — identical to calling the underlying `routes.*` helpers directly.
 *
 * Use this anywhere a navigation could happen from inside the selection options pages so
 * we don't accidentally drop out of the webview shell on save / delete / back / etc.
 */
export function useSelectionOptionRoutes() {
    const webview = useWebviewContext();

    return useMemo(
        () => ({
            isWebview: !!webview,
            detailsLink: (
                selectionOptionId: number,
                jobId: number,
                stateOverride?: SelectionOptionStateOverride,
                selectionOptionGroupId?: number,
                useModal?: boolean,
            ): string =>
                webview
                    ? routes.selectionOptions.getWebviewDetailsLink(
                          selectionOptionId,
                          jobId,
                          webview,
                          stateOverride,
                          selectionOptionGroupId,
                          useModal,
                      )
                    : routes.selectionOptions.getDetailsLink(
                          selectionOptionId,
                          jobId,
                          stateOverride,
                          selectionOptionGroupId,
                          useModal,
                      ),
            listLink: (tab?: SelectionListContainerTabs): string =>
                webview
                    ? routes.selection.getWebviewListLink(webview, tab)
                    : routes.selection.getListLink(tab),
        }),
        [webview],
    );
}
